/* eslint-disable consistent-return */
import { apiPaths, getApiPath, httpStatus } from '../../constants'
import { Api } from '../../middlewares'
import { Logger, LoggerTypes } from '../../utils'

// -- CREATE
export const CREATE_SCENARIO_SUCCESS = 'CREATE_SCENARIO_SUCCESS'
export const CREATE_SCENARIO_FAILURE = 'CREATE_SCENARIO_FAILURE'

// -- UPDATE
export const UPDATE_SCENARIO_SUCCESS = 'UPDATE_SCENARIO_SUCCESS'
export const UPDATE_SCENARIO_FAILURE = 'UPDATE_SCENARIO_FAILURE'

// -- GET (array of objects)
export const LIST_SCENARIO_SUCCESS = 'LIST_SCENARIO_SUCCESS'
export const LIST_SCENARIO_FAILURE = 'LIST_SCENARIO_FAILURE'

// -- GET (one object)
export const GET_SCENARIO_SUCCESS = 'GET_SCENARIO_SUCCESS'
export const GET_SCENARIO_FAILURE = 'GET_SCENARIO_FAILURE'

// -- DELETE
export const DELETE_SCENARIO_SUCCESS = 'DELETE_SCENARIO_SUCCESS'
export const DELETE_SCENARIO_FAILURE = 'DELETE_SCENARIO_FAILURE'

// -- CLONE
export const CLONE_SCENARIO_SUCCESS = 'CLONE_SCENARIO_SUCCESS'
export const CLONE_SCENARIO_FAILURE = 'CLONE_SCENARIO_FAILURE'

// -- IMPORT
export const IMPORT_SCENARIO_SUCCESS = 'IMPORT_SCENARIO_SUCCESS'
export const IMPORT_SCENARIO_FAILURE = 'IMPORT_SCENARIO_FAILURE'

// -- SEARCH
export const SEARCH_SCENARIOS = 'SEARCH_SCENARIOS'

// -- IMPORT GLOBALS
export const IMPORT_SCENARIO_GLOBALS_SUCCESS = 'IMPORT_SCENARIO_GLOBALS_SUCCESS'
export const IMPORT_SCENARIO_GLOBALS_FAILURE = 'IMPORT_SCENARIO_GLOBALS_FAILURE'

// ____________________________________________________________

/**
 * Search for a scenario.
 *
 * @param {object} terms Text to be searched within the scenario name.
 * @returns {object}
 */
export const searchScenarios = (terms) => ({
  type: SEARCH_SCENARIOS,
  data: terms
})

/**
 * Create a new scenario broadcast two types of events:
 * - CREATE_SCENARIO_SUCCESS: in case of success
 * - CREATE_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id.
 * @param {object} scenario Object to create in the database, Ex: { id: 123, name: '', description: '', ...}
 * @returns {Promise<object>}
 */
export const createScenario = (pid, scenario) => async (dispatch) => {
  const path = getApiPath(apiPaths.SCENARIO_CREATE, { pid })
  const api = new Api(path)
  const result = await api.create(scenario)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.createScenario()', { path, pid, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CREATE_SCENARIO_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: CREATE_SCENARIO_FAILURE,
    data: result.data
  })
}

/**
 * Update the values of a scenario broadcast two types of events:
 * - UPDATE_SCENARIO_SUCCESS: in case of success
 * - UPDATE_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project Id
 * @param {object} scenario Object to update in the database, Ex: { id: 123, name: '', description: '', ...}
 * @returns {Promise<object>}
 */
export const updateScenario = (pid, scenario) => async (dispatch) => {
  if (!scenario) {
    return
  }

  const path = getApiPath(apiPaths.SCENARIO_UPDATE, { pid, sid: scenario.id })
  const api = new Api(path)

  const newScenario = Object.assign({}, scenario)
  newScenario.update_user = 'test' // FIX: `update_user` must be present to update the scenario.

  const result = await api.update(newScenario)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.updateScenario()', {
    path,
    result,
    newScenario
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: UPDATE_SCENARIO_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: UPDATE_SCENARIO_FAILURE,
    data: result.data
  })
}

export const getProjectScenarios = (pid) => async () => {
  const path = getApiPath(apiPaths.SCENARIO_LIST, { pid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.getScenarios()', { path, pid, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return result.data.items
  }
  return result.data
}

/**
 * Get the scenarios list form the API and broadcast two types of events:
 * - LIST_SCENARIO_SUCCESS: in case of success
 * - LIST_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project id.
 * @param {boolean} notify Indicates if the result must be notified (true) to listeners or not (false).
 * @returns {Promise<object>}
 */
export const getScenarios = (pid) => async (dispatch) => {
  const path = getApiPath(apiPaths.SCENARIO_LIST, { pid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.getScenarios()', { path, pid, result })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: LIST_SCENARIO_SUCCESS,
      data: result.data.items || []
    })
  }
  return dispatch({
    type: LIST_SCENARIO_FAILURE,
    data: result.data
  })
}

/**
 * Get a specific scenario and broadcast two types of events:
 * - GET_SCENARIO_SUCCESS: in case of success
 * - GET_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project's ID.
 * @param {uuid} sid Scenario's ID.
 * @returns {Promise<object>}
 */
export const getScenario = (pid, sid) => async (dispatch) => {
  const path = getApiPath(apiPaths.SCENARIO_GET, { pid, sid })
  const api = new Api(path)
  const result = await api.get()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.getScenario()', {
    pid,
    sid,
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: GET_SCENARIO_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: GET_SCENARIO_FAILURE,
    data: result.data
  })
}

/**
 * Delete a scenario and broadcast two types of events:
 * - DELETE_SCENARIO_SUCCESS: in case of success
 * - DELETE_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project's ID.
 * @param {object} scenario Scenario to be deleted.
 * @returns {Promise<object>}
 */
export const deleteScenario = (pid, scenario) => async (dispatch) => {
  const sid = scenario?.id
  const path = getApiPath(apiPaths.SCENARIO_DELETE, { pid, sid })
  const api = new Api(path)
  const result = await api.delete()

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.deleteScenario()', {
    pid,
    sid,
    path,
    scenario,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: DELETE_SCENARIO_SUCCESS,
      data: scenario
    })
  }
  return dispatch({
    type: DELETE_SCENARIO_FAILURE,
    data: result.data
  })
}

/**
 * Clone a scenario and broadcast two types of events:
 * - CLONE_SCENARIO_SUCCESS: in case of success
 * - CLONE_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project's ID.
 * @param {object} scenario Scenario to be cloned.
 * @returns {Promise<object>}
 */
export const cloneScenario = (pid, scenario) => async (dispatch) => {
  const sid = scenario?.id
  const path = getApiPath(apiPaths.SCENARIO_CLONE, { pid, sid })
  const api = new Api(path)
  const result = await api.create({})

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.cloneScenario()', {
    pid,
    sid,
    path,
    scenario,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: CLONE_SCENARIO_SUCCESS,
      data: scenario
    })
  }
  return dispatch({
    type: CLONE_SCENARIO_FAILURE,
    data: result.data
  })
}

/**
 * Import a scenario from another project and broadcast two types of events:
 * - IMPORT_SCENARIO_SUCCESS: in case of success
 * - IMPORT_SCENARIO_FAILURE: in case of failure.
 *
 * @param {uuid} pid Project ID of the scenario to import.
 * @param {object} sid Scenario ID to be imported.
 * @param {object} targetProject The target project where the scenario will be imported.
 * @returns {Promise<object>}
 */
export const importScenario = (pid, sid, targetProject) => async (dispatch) => {
  const path = getApiPath(apiPaths.SCENARIO_IMPORT, { pid, sid })
  const api = new Api(path)
  const result = await api.create(targetProject)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.importScenario()', {
    pid,
    sid,
    targetProject,
    path,
    result
  })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: IMPORT_SCENARIO_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: IMPORT_SCENARIO_FAILURE,
    data: result.data
  })
}

/**
 * Import a list of global attribute Ids within a scenario and broadcast two type of events:
 * - IMPORT_SCENARIO_GLOBALS_SUCCESS: in case of success.
 * - IMPORT_SCENARIO_GLOBALS_FAILURE: in case of failure.
 *
 * @param {Object} listOfIds to import.
 * @param {number} pid Project's ID.
 * @param {number} sid Scenario's ID.
 * @returns {Promise<object>}
 */
export const importScenarioGlobals = (listOfIds, pid, sid) => async (dispatch) => {
  const path = getApiPath(apiPaths.SCENARIOS_IMPORT_GLOBAL, { pid, sid })
  const api = new Api(path)
  const result = await api.update(listOfIds)

  Logger.trace(LoggerTypes.ACTION, '[ACTION] scenariosActions.importScenarioGlobals()', { path, pid, sid, listOfIds })

  if (result.status >= httpStatus.OK && result.status < httpStatus.AMBIGUOUS) {
    return dispatch({
      type: IMPORT_SCENARIO_GLOBALS_SUCCESS,
      data: result.data
    })
  }
  return dispatch({
    type: IMPORT_SCENARIO_GLOBALS_FAILURE,
    data: result.data
  })
}
